/**
 * The get-going steps: what setup could not do for the person, as a list.
 *
 * Setup makes the records, the lists a form picks from, and the cards. It cannot
 * make the people, put the shop's own customers in Customers, or write the
 * first sale. Those three are the person's work, and on day zero the Dashboard
 * names them as tiles above the strip (THE-DASHBOARD-ELEMENTS.md row E1,
 * docs/design/dashboard-get-going, direction C).
 *
 * EVERY STEP IS DERIVED, NEVER STORED. A list step is "this list has no rows",
 * read off the catalog the phone already holds; the people step is "one member
 * and no open invitation", read off the member list; the first-entry step is
 * "no record has a row". So a customer added on the owner's other phone ticks
 * the tile on this one. Only three facts are written down, per business, on this
 * phone: which steps were SEEN pending (so a step that has since been done can
 * still be drawn with its tick), which were SKIPPED, and whether the card was
 * HIDDEN. `setup-progress.ts` is the store; this file never reads a database.
 *
 * A SKIP LEAVES THE COUNT. "0 of 5" becomes "0 of 4": a skip says "not a step
 * for me", and the meter measures the steps that are. A done step keeps its
 * place, so the count never lies about what was done.
 *
 * THE ORDER IS THE ORDER ASKED FOR: people, then the lists in catalog order,
 * then the entry. The Rules board on the canvas argues for lists first; that
 * is one line in `deriveSetupSteps` when the day comes.
 *
 * Pure, so `setup-steps.test.ts` runs the real rules with no device.
 */
import type { RecordListItem } from '@alluzo/shared';

/** The three facts this phone writes down about the card, per business. */
export type SetupProgress = {
  /** Step ids that were pending at some draw. A seen step that is no longer pending is done. */
  readonly seen: readonly string[];
  /** Step ids the person crossed off. Gone from the card and from the count. */
  readonly skipped: readonly string[];
  /** True once the last step landed or Hide was tapped. The card never returns. */
  readonly hidden: boolean;
};

export const EMPTY_PROGRESS: SetupProgress = { seen: [], skipped: [], hidden: false };

/**
 * The stored JSON as a progress, or the empty one for anything that is not.
 *
 * Forgiving on purpose: a row an older build wrote in another shape must draw
 * the card fresh rather than take the Dashboard down.
 */
export const parseProgress = (json: string | null): SetupProgress => {
  if (json === null) return EMPTY_PROGRESS;
  try {
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_PROGRESS;
    const held = parsed as Record<string, unknown>;
    const ids = (value: unknown): string[] =>
      Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string') : [];
    return { seen: ids(held.seen), skipped: ids(held.skipped), hidden: held.hidden === true };
  } catch {
    return EMPTY_PROGRESS;
  }
};

/** What the member list says, once it has landed. Null while it has not. */
export type PeopleFacts = {
  readonly members: number;
  /** Invitations minted and not yet claimed. One of these is already an answer. */
  readonly openInvites: number;
};

/** One of the team reads, as `useTenantRead` holds it: only a ready one carries rows. */
type PeopleRead = { readonly status: 'ready'; readonly value: readonly unknown[] } | { readonly status: 'loading' | 'offline' | 'failed' };

/**
 * The people facts from the member list and the open invitations, or null
 * until both have answered. Every invitation `GET /v1/invites` lists counts,
 * a link nobody opened yet and one past its seven days alike: sending it was
 * the person's part.
 */
export const peopleFactsOf = (team: PeopleRead, invites: PeopleRead): PeopleFacts | null =>
  team.status === 'ready' && invites.status === 'ready' ? { members: team.value.length, openInvites: invites.value.length } : null;

export type SetupFacts = {
  readonly records: readonly RecordListItem[];
  /** Null until the team and its invitations answer, and null for as long as they cannot. */
  readonly people: PeopleFacts | null;
  /** `invite_people`. Without it there is no people step to draw. */
  readonly mayInvite: boolean;
};

export type SetupStep =
  | { readonly id: 'invite'; readonly kind: 'invite'; readonly done: boolean }
  | { readonly id: string; readonly kind: 'list'; readonly record: RecordListItem; readonly done: boolean }
  | { readonly id: 'first'; readonly kind: 'first'; readonly record: RecordListItem; readonly done: boolean };

export type SetupCard = {
  /** In the order drawn. Skipped steps are absent. */
  readonly steps: readonly SetupStep[];
  readonly done: number;
  readonly total: number;
  /**
   * Step ids pending now that `progress.seen` does not hold yet. The hook
   * writes these down so a step done tomorrow still shows its tick.
   */
  readonly newlySeen: readonly string[];
  /**
   * True when every drawn step is done and nothing is still being checked:
   * the card has done its work and the hook writes `hidden`. False while the
   * member list is in flight, because the people step may still join.
   */
  readonly finished: boolean;
  /**
   * True when there is nothing honest to draw this frame: no steps at all, or
   * every step drawn is done but the people step cannot be checked yet. The
   * card renders nothing and writes nothing.
   */
  readonly silent: boolean;
};

export const listStepId = (recordId: string): string => `list:${recordId}`;

/** Whether to OFFER adding to this record. True on a null grant, as `record-grant.ts` rules. */
const mayAdd = (record: RecordListItem): boolean =>
  record.grant === null || record.grant.capabilities.includes('add_entry');

/**
 * The record the first-entry step points at: the first money-in one in catalog
 * order, else the first of them. A sale is the row a shop is most likely to have
 * by tonight, and the one every default card reads.
 */
const firstRecordOf = (records: readonly RecordListItem[]): RecordListItem | null =>
  records.find((one) => one.direction === 'in') ?? records[0] ?? null;

type Candidate = { readonly step: SetupStep; readonly pending: boolean };

export const deriveSetupSteps = (facts: SetupFacts, progress: SetupProgress): SetupCard => {
  const candidates: Candidate[] = [];

  // People: only somebody who may invite is asked to, and only once the list
  // has landed. A pending invitation counts as the answer: the person did
  // their part, and the tick should not wait on somebody else's phone.
  if (facts.mayInvite && facts.people !== null) {
    const pending = facts.people.members <= 1 && facts.people.openInvites === 0;
    candidates.push({ step: { id: 'invite', kind: 'invite', done: !pending }, pending });
  }

  // Lists: one step per list with nothing in it. A seeded list — payment
  // modes, expense categories — has rows and is never a step.
  for (const record of facts.records) {
    if (record.kind !== 'directory' || !mayAdd(record)) continue;
    const pending = record.entryCount === 0;
    candidates.push({
      step: { id: listStepId(record.id), kind: 'list', record, done: !pending },
      pending,
    });
  }

  // The first entry: one step, while no record has a row.
  const records = facts.records.filter((one) => one.kind === 'record' && mayAdd(one));
  const firstRecord = firstRecordOf(records);
  if (firstRecord !== null) {
    const pending = records.every((one) => one.entryCount === 0);
    candidates.push({
      step: { id: 'first', kind: 'first', record: firstRecord, done: !pending },
      pending,
    });
  }

  const skipped = new Set(progress.skipped);
  const seen = new Set(progress.seen);

  // Drawn: pending now, or seen pending once and done since. Never skipped.
  const drawn = candidates.filter(
    ({ step, pending }) => !skipped.has(step.id) && (pending || seen.has(step.id)),
  );
  const steps = drawn.map(({ step }) => step);
  const done = steps.filter((step) => step.done).length;
  const newlySeen = drawn
    .filter(({ step, pending }) => pending && !seen.has(step.id))
    .map(({ step }) => step.id);

  const peopleUnchecked = facts.mayInvite && facts.people === null;
  const allDone = steps.length > 0 && done === steps.length;

  return {
    steps,
    done,
    total: steps.length,
    newlySeen,
    finished: allDone && !peopleUnchecked,
    silent: steps.length === 0 || (allDone && peopleUnchecked),
  };
};

/** The progress after a skip. Idempotent, so a double tap writes the same row. */
export const withSkipped = (progress: SetupProgress, stepId: string): SetupProgress =>
  progress.skipped.includes(stepId)
    ? progress
    : { ...progress, skipped: [...progress.skipped, stepId] };

/** The progress after this frame's pending steps are written down. */
export const withSeen = (progress: SetupProgress, ids: readonly string[]): SetupProgress => {
  const fresh = ids.filter((id) => !progress.seen.includes(id));
  return fresh.length === 0 ? progress : { ...progress, seen: [...progress.seen, ...fresh] };
};

export const withHidden = (progress: SetupProgress): SetupProgress =>
  progress.hidden ? progress : { ...progress, hidden: true };
